/**
 * Hagatna Admin — Formatting Utilities
 */

// Format currency (EGP / AED)
export function formatCurrency(amount, currency = 'EGP', locale = 'ar-EG') {
  if (amount == null) return '—'
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount)
}

// Format numbers
export function formatNumber(num, locale = 'ar-EG') {
  if (num == null) return '—'
  return new Intl.NumberFormat(locale).format(num)
}

// Format date (Arabic)
export function formatDate(dateStr, locale = 'ar-EG') {
  if (!dateStr) return '—'
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(dateStr))
}

// Format datetime
export function formatDateTime(dateStr, locale = 'ar-EG') {
  if (!dateStr) return '—'
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(dateStr))
}

// Relative time (e.g. "منذ 3 ساعات")
export function formatRelativeTime(dateStr, locale = 'ar-EG') {
  if (!dateStr) return '—'
  const diff = Date.now() - new Date(dateStr).getTime()
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  const seconds = Math.floor(diff / 1000)
  if (seconds < 60) return rtf.format(-seconds, 'second')
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return rtf.format(-minutes, 'minute')
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return rtf.format(-hours, 'hour')
  const days = Math.floor(hours / 24)
  if (days < 30) return rtf.format(-days, 'day')
  const months = Math.floor(days / 30)
  if (months < 12) return rtf.format(-months, 'month')
  return rtf.format(-Math.floor(months / 12), 'year')
}

// Get localized field (en/ar object)
export function getLocalizedField(field, lang = 'ar') {
  if (!field) return ''
  if (typeof field === 'string') return field
  return field[lang] || field['en'] || ''
}

// Truncate text
export function truncate(text, maxLength = 50) {
  if (!text) return ''
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text
}

// Order status label
export const ORDER_STATUS_LABELS = {
  pending:    { ar: 'قيد الانتظار', en: 'Pending',    class: 'badge-pending' },
  confirmed:  { ar: 'مؤكد',         en: 'Confirmed',  class: 'badge-confirmed' },
  processing: { ar: 'جارٍ التجهيز', en: 'Processing', class: 'badge-pending' },
  shipped:    { ar: 'تم الشحن',      en: 'Shipped',   class: 'badge-shipped' },
  delivered:  { ar: 'تم التسليم',   en: 'Delivered',  class: 'badge-delivered' },
  cancelled:  { ar: 'ملغي',          en: 'Cancelled', class: 'badge-cancelled' },
}

// Payment status label — separate field from order (fulfillment) status
export const PAYMENT_STATUS_LABELS = {
  pending:   { ar: 'قيد الانتظار', en: 'Pending',   class: 'badge-pending' },
  completed: { ar: 'مدفوع',        en: 'Paid',      class: 'badge-paid' },
  refunded:  { ar: 'مُسترد',       en: 'Refunded',  class: 'badge-refunded' },
  failed:    { ar: 'فشل',          en: 'Failed',    class: 'badge-rejected' },
}

// Vendor status label
export const VENDOR_STATUS_LABELS = {
  pending:   { ar: 'قيد المراجعة', en: 'Pending',   class: 'badge-pending' },
  approved:  { ar: 'موافق عليه',   en: 'Approved',  class: 'badge-approved' },
  rejected:  { ar: 'مرفوض',        en: 'Rejected',  class: 'badge-rejected' },
  suspended: { ar: 'موقوف',        en: 'Suspended', class: 'badge-suspended' },
}

export const PRODUCT_STATUS_LABELS = {
  active:           { ar: 'نشط',                 en: 'Active',           class: 'badge-approved' },
  draft:            { ar: 'مسودة',               en: 'Draft',            class: 'badge-draft' },
  pending_approval: { ar: 'في انتظار الموافقة', en: 'Pending Approval', class: 'badge-pending' },
  rejected:         { ar: 'مرفوض',               en: 'Rejected',         class: 'badge-rejected' },
  archived:         { ar: 'مؤرشف',               en: 'Archived',         class: 'badge-suspended' },
}

// User role label
export const ROLE_LABELS = {
  admin:    { ar: 'مدير',   en: 'Admin' },
  vendor:   { ar: 'بائع',   en: 'Vendor' },
  customer: { ar: 'عميل',   en: 'Customer' },
}

// Review status label
export const REVIEW_STATUS_LABELS = {
  pending:  { ar: 'قيد المراجعة', en: 'Pending',  class: 'badge-pending' },
  approved: { ar: 'موافق عليه',   en: 'Approved', class: 'badge-approved' },
  rejected: { ar: 'مرفوض',        en: 'Rejected', class: 'badge-rejected' },
}
